import type { CSSProperties, ReactNode } from "react";

/**
 * The checkout's own vocabulary: a handful of pieces every part of the page
 * is built from, all reading the same `--lc-*` variables, so nothing on the
 * page has a second font, a second radius or a second idea of small text.
 * Lissen has no chrome of its own here — only a small mark and a word.
 */

export const size = {
  small: "0.875em",
  tiny: "0.8125em",
  heading: "1.25em",
} as const;

export const CONTROL_HEIGHT = 44;

export const text = {
  label: {
    color: "var(--lc-muted)",
    fontSize: size.small,
    lineHeight: 1.3,
  } as CSSProperties,
  hint: {
    color: "var(--lc-muted)",
    fontSize: size.tiny,
    lineHeight: 1.4,
    margin: 0,
  } as CSSProperties,
  strong: { fontWeight: 600 } as CSSProperties,
};

const buttonBase: CSSProperties = {
  alignItems: "center",
  appearance: "none",
  boxSizing: "border-box",
  cursor: "pointer",
  display: "flex",
  font: "inherit",
  gap: "var(--lc-space)",
  justifyContent: "center",
  lineHeight: 1.2,
  minHeight: CONTROL_HEIGHT,
  padding: "0 calc(var(--lc-space) * 2)",
  width: "100%",
};

export const button = {
  primary: {
    ...buttonBase,
    background: "var(--lc-button-background)",
    border:
      "var(--lc-border-width) var(--lc-border-style) var(--lc-button-border)",
    borderRadius: "var(--lc-button-radius)",
    boxShadow: "var(--lc-button-shadow)",
    color: "var(--lc-button-text)",
    fontWeight: "var(--lc-button-weight)",
  } as CSSProperties,
  secondary: {
    ...buttonBase,
    background: "var(--lc-button2-background)",
    border:
      "var(--lc-border-width) var(--lc-border-style) var(--lc-button2-border)",
    borderRadius: "var(--lc-button2-radius)",
    boxShadow: "var(--lc-button2-shadow)",
    color: "var(--lc-button2-text)",
    fontWeight: "var(--lc-button2-weight)",
  } as CSSProperties,
  /** Apple Pay and Google Pay: their own black, our shape. */
  wallet: {
    ...buttonBase,
    background: "#000",
    border: "var(--lc-border-width) var(--lc-border-style) #000",
    borderRadius: "var(--lc-button-radius)",
    color: "#fff",
    fontWeight: 600,
  } as CSSProperties,
  /** Text-only, inline, for "Resend code" and the like. */
  link: {
    appearance: "none",
    background: "none",
    border: 0,
    color: "var(--lc-muted)",
    cursor: "pointer",
    font: "inherit",
    fontSize: size.tiny,
    padding: 0,
    textDecoration: "underline",
  } as CSSProperties,
  /** The small square for − and +. */
  step: {
    ...buttonBase,
    background: "var(--lc-button2-background)",
    border:
      "var(--lc-border-width) var(--lc-border-style) var(--lc-button2-border)",
    borderRadius: "var(--lc-radius-sm)",
    color: "var(--lc-button2-text)",
    fontWeight: 600,
    height: 32,
    minHeight: 32,
    padding: 0,
    width: 32,
  } as CSSProperties,
};

export const input: CSSProperties = {
  background: "var(--lc-input-background)",
  border:
    "var(--lc-border-width) var(--lc-border-style) var(--lc-input-border)",
  borderRadius: "var(--lc-input-radius)",
  boxSizing: "border-box",
  color: "var(--lc-input-text)",
  font: "inherit",
  // Matches the card fields: taller when Stripe floats its labels.
  minHeight: "var(--lc-input-height)",
  padding: "0 calc(var(--lc-space) * 1.5)",
  width: "100%",
};

/** A selectable row: a saved card, an option. Same border and radius as a field. */
export const choice = (selected: boolean): CSSProperties => ({
  alignItems: "center",
  background: "var(--lc-input-background)",
  border: `var(--lc-border-width) var(--lc-border-style) ${selected ? "var(--lc-accent)" : "var(--lc-input-border)"}`,
  borderRadius: "var(--lc-input-radius)",
  boxShadow: selected ? "var(--lc-shadow-focus)" : "none",
  boxSizing: "border-box",
  cursor: "pointer",
  display: "flex",
  gap: "calc(var(--lc-space) * 1.5)",
  minHeight: CONTROL_HEIGHT,
  padding: "0 calc(var(--lc-space) * 1.5)",
});

export function Busy({ on, children }: { on: boolean; children: ReactNode }) {
  return <span style={{ opacity: on ? 0.6 : 1 }}>{children}</span>;
}

/** A labelled block: the label above, the content below, one gap. */
export function Field({
  label,
  children,
  trailing,
  grow = false,
}: {
  label: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  /** Take the remaining height, so a child can pin itself to the bottom. */
  grow?: boolean;
}) {
  return (
    <div
      style={{
        display: grow ? "flex" : "grid",
        flexDirection: "column",
        flex: grow ? "1 1 auto" : undefined,
        gap: "calc(var(--lc-space) * 0.75)",
      }}
    >
      {label || trailing ? (
        <div
          style={{
            alignItems: "baseline",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span style={text.label}>{label}</span>
          {trailing}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** A tick with a sentence and a quieter second line. */
export function Tick({
  checked,
  onChange,
  disabled,
  children,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <label
      style={{
        alignItems: "flex-start",
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        fontSize: size.small,
        gap: "var(--lc-space)",
        lineHeight: 1.35,
      }}
    >
      {/* Drawn by the component's own stylesheet (see Frame), in the
          field colours rather than the browser's white box. */}
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        style={{ flex: "none", margin: "2px 0 0" }}
      />
      <span>
        <span style={text.strong}>{children}</span>
        {hint ? (
          <span style={{ ...text.hint, display: "block", marginTop: 2 }}>
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/**
 * A loading piece: the shape of what is coming, shimmering. Every container
 * draws one of these in its own place until its own content is ready, so the
 * page is never a single big box nor an empty frame waiting for pieces.
 */
export const SHIMMER_KEYFRAMES =
  "@keyframes lc-shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}" +
  "@media (prefers-reduced-motion: reduce){[data-lc-shimmer]{animation:none!important}}";
export const shimmer = (radius = "var(--lc-input-radius)"): CSSProperties => ({
  animation: "lc-shimmer 1.4s linear infinite",
  background:
    "linear-gradient(90deg, var(--lc-input-background) 25%, color-mix(in srgb, var(--lc-text) 7%, var(--lc-input-background)) 50%, var(--lc-input-background) 75%)",
  backgroundSize: "200% 100%",
  borderRadius: radius,
});
